import type { SVGProps } from "react";
import { cn } from "../../lib/utils";

// Leading segment first; each following one sits 45° further back.
const SEGMENT_OPACITIES = [1, 0.875, 0.75, 0.625, 0.5, 0.375, 0.25, 0.15];

/**
 * Loading throbber: an 8-segment ring that rotates in 45° steps (see `.throbber`
 * in chat.css). Each step lands the ring on itself, so only the highlight
 * moves, at 10 changes per second instead of every frame. A smoothly rotating
 * icon keeps the GPU busy (~50% on a mid-range laptop), this one ~3-8%
 * (https://nh2.me/low-fps-spinners/low-fps-spinners.html). It is SVG rather
 * than HTML segments so the whole thing rotates as one layer without wobbling
 * at small sizes.
 *
 * Size and colour come from `className` like any icon (`h-3 w-3`, `text-…`);
 * without one it is 16px.
 */
export function Throbber({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeDasharray="3.4 34.3"
      aria-hidden="true"
      className={cn("throbber shrink-0", className)}
      {...props}
    >
      {SEGMENT_OPACITIES.map((opacity, i) => (
        <circle
          key={i}
          cx={8}
          cy={8}
          r={6}
          opacity={opacity}
          transform={`rotate(${-106.2 - i * 45} 8 8)`}
        />
      ))}
    </svg>
  );
}
